import { Theme } from '@mui/material';
import { createTheme } from '@mui/material/styles';

/**
 * Comprehensive styles object for the entire BlindTestDashboard component
 */
export const createBlindTestDashboardStyles = (theme: Theme) => ({
  // Main layout styles
  mainContainer: {
    p: 4,
    "& .MuiPaper-root": {
      boxSizing: "border-box",
    },
    "& .MuiList-root": {
      boxSizing: "border-box",
    },
    "& .MuiListItemButton-root": {
      boxSizing: "border-box",
    },
    "& .MuiCollapse-root": {
      boxSizing: "border-box",
    },
  },
  mainSidebar: {
    flexShrink: 0
  },
  contentArea: {
    width: "100%",
    pl: 2
  },

  // Active card styles
  activeCard: {
    background: "linear-gradient(90deg, rgba(25, 118, 210, 0.1) 5%, rgba(255, 255, 255, 0.95) 100%)",
    boxShadow: "0 8px 10px rgba(0, 123, 255, 0.3)",
    borderLeft: "4px solid #1976d2",
    transform: "scale(1.04)",
    position: "relative",
    zIndex: 10,
    transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
  },

  // Navigation sidebar styles
  navigation: {
    sidebar: {
      width: 280,
      height: "100%",
      overflowY: "auto",
      bgcolor: "background.paper",
      borderRadius: "8px",
      "& .MuiListItemButton-root": {
        borderRadius: "4px",
        mx: 1,
        width: "calc(100% - 16px)",
      },
    },
    sidebarLoading: {
      width: 280,
      height: "100%",
      overflowY: "auto",
      bgcolor: "background.paper",
      borderRadius: "8px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start",
      pt: 10,
    },
    header: {
      p: 2,
      borderBottom: `1px solid ${theme.palette.divider}`
    },
    list: {
      width: "100%",
      py: 1,
    },
    loadingText: {
      mt: 2,
      color: theme.palette.text.secondary,
      mb: 15
    }
  },

  // Navigation item styles
  navItem: {
    button: (level: number, isSelected: boolean) => ({
      pl: level * 2 + 1,
      py: 1,
      borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : "none",
      bgcolor: isSelected ? `${theme.palette.primary.light}20` : "transparent",
      "&:hover": {
        bgcolor: `${theme.palette.primary.light}10`,
      },
      transition: "background-color 0.2s ease",
      marginY: 0.3,
    }),
    text: {
      primary: (level: number, isSelected: boolean) => ({
        fontWeight: isSelected ? 600 : level === 3 ? 500 : 400,
        color: isSelected
          ? theme.palette.primary.main
          : level === 0
          ? theme.palette.text.primary
          : theme.palette.text.secondary,
      }),
      secondary: {
        mt: -0.5,
        fontWeight: 300,
        color: theme.palette.text.secondary,
      }
    },
    expandIcon: {
      color: theme.palette.primary.main,
      opacity: 0.8
    }
  },

  // Position summary card styles
  card: {
    container: (isActive: boolean, isClickable: boolean, todoCount: number) => ({
      width: 338,
      minHeight: 220,
      padding: 2,
      borderRadius: 1,
      transition: "all 200ms ease",
      borderLeft: !isActive && todoCount !== 0 ? `4px solid ${theme.palette.warning.main}` : "none",
      display: "flex",
      flexDirection: "column",
      "&:hover": {
        boxShadow: 6,
        cursor: isClickable ? "pointer" : "not-allowed",
        opacity: isClickable ? 1 : 0.7,
      },
    }),
    content: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
    },
    comingSoonChip: {
      marginTop: "-26px",
      position: "absolute"
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      mb: 1.5,
    },
    title: {
      mb: 0.5
    },
    processStage: {
      display: "block",
      opacity: 0.7,
      fontWeight: 600
    },
    productLine: {
      mt: 0.5,
      color: "primary.main",
      fontWeight: 500
    },
    datasetId: {
      display: "block",
      mt: 1,
      opacity: 0.5,
      letterSpacing: "normal",
    },
    chipStack: {
      spacing: 0.5,
      minWidth: 130
    },
    chips: {
      emelieBts: {
        backgroundColor: theme.palette.success.main,
        color: "white",
        fontSize: "0.75rem",
        fontWeight: 600,
      },
      emelieAcceptable: {
        backgroundColor: "#0f6cbd",
        color: "white",
        fontSize: "0.75rem",
        fontWeight: 600,
      },
      sme: {
        backgroundColor: theme.palette.warning.main,
        color: "white",
        fontSize: "0.75rem",
        fontWeight: 600,
      },
      inProgress: {
        fontSize: "0.75rem"
      }
    },
    imageContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      py: 2,
    },
    image: {
      maxWidth: 120,
      maxHeight: 120,
      objectFit: "contain",
    },
    footer: {
      mt: "auto",
      pt: 2,
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    footerActions: {
      direction: "row",
      alignItems: "center",
      spacing: 1
    },
    footerBox: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      width: "100%",
    }
  },

  // Content display styles
  content: {
    header: {
      pb: 2
    },
    title: {
      mt: 2,
      fontWeight: "medium",
      color: theme.palette.primary.main,
    },
    divider: {
      mb: 4,
      boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      height: "1px",
      width: "98%",
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "200px"
    },
    loadingText: {
      ml: 2
    },
    cardsContainer: {
      display: "flex",
      flexWrap: "wrap",
      gap: 3,
    },
    emptyResults: {
      width: "100%",
      textAlign: "center",
      p: 4
    }
  },

  // Snackbar styles
  snackbar: {
    anchorOrigin: {
      vertical: "bottom" as const,
      horizontal: "center" as const
    }
  }
});

// Constants for active card styles
export const ACTIVE_CARD_STYLES = {
  background: "linear-gradient(90deg, rgba(25, 118, 210, 0.1) 5%, rgba(255, 255, 255, 0.95) 100%)",
  boxShadow: "0 8px 10px rgba(0, 123, 255, 0.3)",
  borderLeft: "4px solid #1976d2",
  transform: "scale(1.04)",
  position: "relative",
  zIndex: 10,
  transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
};

/**
 * Custom theme with active card styles
 */
export const createBlindTestDashboardTheme = () => createTheme({
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          "&.active-card": ACTIVE_CARD_STYLES,
        },
      },
    },
  },
});

/**
 * Global CSS styles for active card
 */
export const getBlindTestDashboardGlobalStyles = () => `
  .active-card {
    background: ${ACTIVE_CARD_STYLES.background} !important;
    box-shadow: ${ACTIVE_CARD_STYLES.boxShadow} !important;
    border-left: ${ACTIVE_CARD_STYLES.borderLeft} !important;
    transform: ${ACTIVE_CARD_STYLES.transform} !important;
    position: ${ACTIVE_CARD_STYLES.position};
    z-index: ${ACTIVE_CARD_STYLES.zIndex};
    transition: ${ACTIVE_CARD_STYLES.transition};
  }
`;

/**
 * Helper function to get navigation item button styles
 */
export const getNavItemButtonStyles = (theme: Theme, level: number, isSelected: boolean) => ({
  pl: level * 2 + 1,
  py: 1,
  borderLeft: isSelected ? `3px solid ${theme.palette.primary.main}` : "none",
  bgcolor: isSelected ? `${theme.palette.primary.light}20` : "transparent",
  "&:hover": {
    bgcolor: `${theme.palette.primary.light}10`,
  },
  transition: "background-color 0.2s ease",
  marginY: 0.3,
});

/**
 * Helper function to get navigation item text styles
 */
export const getNavItemTextStyles = (theme: Theme, level: number, isSelected: boolean) => ({
  fontWeight: isSelected ? 600 : level === 3 ? 500 : 400,
  color: isSelected
    ? theme.palette.primary.main
    : level === 0
    ? theme.palette.text.primary
    : theme.palette.text.secondary,
});

/**
 * Helper function to get card container styles
 */
export const getCardContainerStyles = (theme: Theme, isActive: boolean, isClickable: boolean, todoCount: number) => ({
  width: 338,
  minHeight: 220,
  padding: 2,
  borderRadius: 1,
  transition: "all 200ms ease",
  borderLeft: !isActive && todoCount !== 0 ? `4px solid ${theme.palette.warning.main}` : "none",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    boxShadow: 6,
    cursor: isClickable ? "pointer" : "not-allowed",
    opacity: isClickable ? 1 : 0.7,
  },
}); 